import { useState } from 'react';

import { formMeta } from '../data/forms.js';
import { useFormResponses } from '../data/responses.js';
import type { Form } from '../fixtures/domain.js';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { formatDate } from '../util/format.js';
import { FormPreview } from './FormPreview.js';
import { FormResponses } from './FormResponses.js';
import styles from './FormPane.module.css';

export type FormTab = 'questions' | 'responses';
const TABS: [FormTab, string][] = [
  ['questions', 'Questions'],
  ['responses', 'Responses'],
];

/**
 * One form, read-only: the questions as the person answering them will see
 * them, and every answer to them across all chats on the Responses tab.
 */
export function FormPane({
  form,
  templatesOpen,
  onToggleTemplates,
  onEdit,
  onDelete,
  tab,
  onTab,
  reviewId,
  onReview,
  reviewHost,
  onOpenChat,
  onChatExists,
}: {
  form: Form;
  /** Whether the templates panel is up, and the switch for it — the panel is
   *  the section's, so every screen in the section carries its switch. */
  templatesOpen?: boolean;
  onToggleTemplates?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** The page's, so a link from a chat can land on Responses with an answer open. */
  tab: FormTab;
  onTab: (tab: FormTab) => void;
  /** The response open in the context column, and the slot the page gives it there. */
  reviewId: string | null;
  onReview: (id: string | null) => void;
  reviewHost: HTMLElement | null;
  onOpenChat: (cid: string) => void;
  onChatExists: (cid: string) => Promise<boolean>;
}) {
  const [confirming, setConfirming] = useState(false);
  const responses = useFormResponses(form.id);

  return (
    <div className={styles.pane}>
      <div className={styles.head}>
        <div className={styles.heading}>
          <h2 className={styles.title}>{form.name}</h2>
          {/* "Saved", not "Edited": save stamps updatedAt on the first write
              too, so an untouched form was reporting an edit that never
              happened. Saved is true of both, and it is the button's word. */}
          <p className={styles.meta}>
            {formMeta(form)} · Saved {formatDate(form.updatedAt)}
          </p>
        </div>
        {confirming ? (
          <div className={styles.confirm}>
            <span className={styles.confirmText}>
              Delete this form? Chats that already have it keep their copy.
            </span>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={onDelete}>
              Delete
            </Button>
          </div>
        ) : (
          <div className={styles.actions}>
            <button type="button" className={styles.action} onClick={onEdit}>
              <Icon name="pencil" size={15} />
              Edit
            </button>
            <button type="button" className={styles.action} onClick={() => setConfirming(true)}>
              <Icon name="trash" size={15} />
              Delete
            </button>
            {/* Last, at the pane's own right edge, where the conversation
                header keeps the switch for its details column. */}
            {onToggleTemplates && (
              <button
                type="button"
                className={templatesOpen ? styles.panelOn : styles.panel}
                aria-pressed={templatesOpen}
                title="Templates"
                aria-label="Templates"
                onClick={onToggleTemplates}
              >
                <Icon name={templatesOpen ? 'panelRightClose' : 'panelRight'} size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Form">
        {TABS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            className={tab === value ? styles.tabActive : styles.tab}
            onClick={() => onTab(value)}
          >
            {label}
            {value === 'responses' && !responses.loading && !responses.failed && (
              <span className={styles.tabCount}>{responses.rows.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'responses' ? (
        <div className={styles.responses}>
          <FormResponses
            form={form}
            data={responses}
            reviewId={reviewId}
            onReview={onReview}
            reviewHost={reviewHost}
            onOpenChat={onOpenChat}
            onChatExists={onChatExists}
          />
        </div>
      ) : (
        <div className={styles.scroll}>
          <div className={styles.column}>
            <FormPreview fields={form.fields} />
          </div>
        </div>
      )}
    </div>
  );
}
